import React, { useState } from 'react';
import { useAdmin } from '../../hooks/useAdmin';
import { MemberService } from '../../services/memberService';
import LoadingState from '../shared/LoadingState';
import { Edit, Trash2, Users, X, Plus, Search, Phone, Tag, Sparkles } from 'lucide-react';

export default function AllMembersTable() {
  const { members, meetingTypes, addMember, loading: dataLoading } = useAdmin();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMeetingType, setSelectedMeetingType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const filteredMembers = members.filter(member => {
    const matchesSearch = !searchQuery || 
      member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.phone.includes(searchQuery) ||
      (member.tags && member.tags.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesType = !selectedMeetingType || member.meeting_type_id === selectedMeetingType;
    
    return matchesSearch && matchesType;
  });

  const handleAddMember = async (formData: any) => {
    setLoading(true);
    setError('');

    const result = await addMember(formData);
    
    if (result.data) {
      setShowAddModal(false);
    } else {
      setError(result.error?.message || 'Failed to add member');
    }
    
    setLoading(false);
  };

  const handleEditMember = async (memberId: string, formData: any) => {
    setLoading(true);
    setError('');

    // For now, just show success message (edit functionality can be added later)
    setError('Edit functionality coming soon...');
    setTimeout(() => setError(''), 2000);
    
    setLoading(false);
  };

  const handleDeleteMember = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to delete this member?')) return;

    setLoading(true);
    // For now, just show success message (delete functionality can be added later)
    setError('Delete functionality coming soon...');
    setTimeout(() => setError(''), 2000);
    
    setLoading(false);
  };

  const openEditModal = (member: any) => {
    setEditingMember(member);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setEditingMember(null);
    setError('');
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Members
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, phone, or tags..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
              />
            </div>
          </div>

          {/* Meeting Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Meeting Type
            </label>
            <select
              value={selectedMeetingType}
              onChange={(e) => setSelectedMeetingType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
            >
              <option value="">All Meeting Types</option>
              {meetingTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name} - {type.level}
                </option>
              ))}
            </select>
          </div>

          {/* Add Member Button */}
          <div className="flex items-end sm:col-span-2 md:col-span-1">
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition w-full text-sm font-medium"
            >
              <Plus size={16} />
              <span>Add Member</span>
            </button>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Users size={16} />
            <span>{dataLoading ? 'Loading members...' : `${filteredMembers.length} members found`}</span>
          </div>
          {(searchQuery || selectedMeetingType) && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedMeetingType('');
              }}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Members Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {dataLoading ? (
          <LoadingState title="Fetching Member Directory..." minHeight="min-h-[300px]" />
        ) : filteredMembers.length > 0 ? (
          <div>
            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Meeting Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tags
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-medium">
                              {member.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {member.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <Phone size={14} className="mr-1" />
                          {member.phone}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {member.meeting_types?.name || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {member.tags ? (
                          <div className="flex items-center text-sm text-gray-500">
                            <Tag size={14} className="mr-1" />
                            {member.tags}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No tags</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => openEditModal(member)}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteMember(member.id)}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="sm:hidden space-y-3 p-4">
              {filteredMembers.map((member) => (
                <div key={member.id} className="border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50 transition">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-semibold text-base">
                          {member.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{member.name}</div>
                        <div className="flex items-center text-xs text-gray-500 mt-0.5">
                          <Phone size={12} className="mr-1 text-gray-400" />
                          {member.phone}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => openEditModal(member)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteMember(member.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-100 text-xs">
                    <span className="px-2 py-0.5 font-medium rounded-full bg-blue-100 text-blue-800">
                      {member.meeting_types?.name || 'Unknown'}
                    </span>
                    {member.tags && (
                      <div className="flex items-center text-gray-500">
                        <Tag size={12} className="mr-1 text-gray-400" />
                        <span>{member.tags}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {members.length === 0 ? (
              <div>
                <Users size={48} className="mx-auto mb-4 text-gray-300" />
                <p>No members found in the system.</p>
                <p className="text-sm mt-2">Add your first member to get started!</p>
              </div>
            ) : (
              <div>
                <Search size={48} className="mx-auto mb-4 text-gray-300" />
                <p>No members match your search criteria.</p>
                <p className="text-sm mt-2">Try adjusting your filters.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <MemberModal
          title="Add New Member"
          meetingTypes={meetingTypes}
          onSubmit={handleAddMember}
          onClose={closeModals}
          loading={loading}
        />
      )}

      {/* Edit Member Modal */}
      {editingMember && (
        <MemberModal
          title="Edit Member"
          meetingTypes={meetingTypes}
          initialData={editingMember}
          onSubmit={(data) => handleEditMember(editingMember.id, data)}
          onClose={closeModals}
          loading={loading}
        />
      )}
    </div>
  );
}

// Member Modal Component
interface MemberModalProps {
  title: string;
  meetingTypes: any[];
  initialData?: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
  loading: boolean;
}

function MemberModal({ title, meetingTypes, initialData, onSubmit, onClose, loading }: MemberModalProps) {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    phone: initialData?.phone || '',
    meeting_type_id: initialData?.meeting_type_id || '',
    tags: initialData?.tags || ''
  });
  const [suggestedMember, setSuggestedMember] = useState<any>(null);

  const handlePhoneChange = async (val: string) => {
    setFormData(prev => ({ ...prev, phone: val }));
    const clean = val.trim().replace(/[^0-9]/g, '');
    if (clean.length >= 5 && !initialData) {
      const { data } = await MemberService.findMemberByPhone(clean);
      if (data && data.length > 0) {
        setSuggestedMember(data[0]);
      } else {
        setSuggestedMember(null);
      }
    } else {
      setSuggestedMember(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 transition-opacity">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200">
        {/* Mobile Pull Indicator */}
        <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto my-2 sm:hidden shrink-0" />

        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 border-b border-gray-200 shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition disabled:opacity-50 min-w-[40px] min-h-[40px] flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
              Phone Number *
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
              required
              disabled={loading}
            />
          </div>

          {suggestedMember && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 text-xs flex items-center justify-between shadow-2xs">
              <div>
                <span className="font-bold text-blue-900 flex items-center space-x-1">
                  <Sparkles size={14} className="text-blue-600" />
                  <span>Existing Member Found:</span>
                </span>
                <span className="text-blue-800 font-semibold">{suggestedMember.name} ({suggestedMember.phone})</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    name: suggestedMember.name,
                    tags: suggestedMember.tags || prev.tags
                  }));
                  setSuggestedMember(null);
                }}
                className="px-2.5 py-1 bg-blue-600 text-white rounded-lg font-bold text-xs hover:bg-blue-700 transition shrink-0 ml-2 shadow-2xs"
              >
                Auto-Fill
              </button>
            </div>
          )}

          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
              Meeting Type *
            </label>
            <select
              value={formData.meeting_type_id}
              onChange={(e) => setFormData({ ...formData, meeting_type_id: e.target.value })}
              className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
              required
              disabled={loading}
            >
              <option value="">Select meeting type</option>
              {meetingTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name} - {type.level}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
              Tags (Optional)
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="e.g. Active, VIP, New"
              className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
              disabled={loading}
            />
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2.5 text-xs sm:text-sm font-semibold border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition disabled:opacity-50 min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-blue-700 transition disabled:opacity-50 min-h-[44px] shadow-sm flex items-center justify-center space-x-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <span>{(initialData ? 'Update' : 'Add') + ' Member'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
